/**
 * @typedef {object} Handler
 * @property {string} name - Name of the event
 * @property {function} handler - Registered callback
 * @property {Listenable} source - Listenable that made the registration
 */


export class Listenable {
	constructor() {
	}

	/**
	 * Register event listener for named event
	 * @param {string} eventName
	 * @param {function} handler
	 * @param {object} binding - object to use as this when calling handler
	 * @return {Listenable}
	 */
	on(eventName, handler, binding) {
		if (!this.hasOwnProperty("listeners")) this.listeners = {};
		if (!this.listeners.hasOwnProperty(eventName)) this.listeners[eventName] = [];
		this.listeners[eventName].push({
			handler : handler,
			binding : binding
		});
		return this;
	}

	/**
	 * @param {string} eventName
	 * @param {function} handler - original handler used in on() call
	 * @param {object} binding - original binding used in on() call
	 * @return {Listenable}
	 */
	off(eventName, handler, binding) {
		if (this.hasOwnProperty("listeners") && this.listeners.hasOwnProperty(eventName)) {
			const regs = this.listeners[eventName];
			const idx = regs.findIndex((reg)=>{
				return handler === reg.handler && Listenable.sameBinding(binding, reg.binding);
			});
			if (idx >= 0) {
				//console.log("Handler " + eventName + " removed from index " + idx);
				regs.splice(idx, 1);
			}
		}
		return this;
	}

	fire(eventName, arg) {
		if (this.hasOwnProperty("listeners") && this.listeners.hasOwnProperty(eventName)) {
			for (let reg of this.listeners[eventName]) {
				if (reg.binding) reg.handler.call(reg.binding, this, arg);
				else reg.handler(this, arg);
			}
		}
		return this;
	}

	static sameBinding(b1, b2) {
		if (typeof b1 === "undefined") return typeof b2 === "undefined";
		else return b2 === b1;
	}

	listenerCount(eventName) {
		if (this.hasOwnProperty("listeners")) {
			if (this.listeners.hasOwnProperty(eventName)) {
				return this.listeners[eventName].length;
			}
		}
		return 0;
	}

	/**
	 * Remove all listeners and return old registrations
	 * @return {object}
	 */
	clearAllListeners() {
		const listeners = this.listeners;
		delete this.listeners;
		return listeners;
	}
}